/**
 * Food Analysis Cache
 * Caches ChatGPT food analysis results to improve performance
 */

interface CachedAnalysis {
  foodName: string;
  analysis: any;
  timestamp: number;
  ingredientsText?: string;
}

const CACHE_KEY_PREFIX = 'food_analysis_cache_';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours (updated per requirements)
const MAX_CACHE_SIZE = 100; // Maximum number of cached items

/**
 * Generate cache key from food name and ingredients
 */
function getCacheKey(foodName: string, ingredientsText?: string): string {
  const normalizedName = foodName.trim().toLowerCase();
  const normalizedIngredients = ingredientsText?.trim().toLowerCase() || '';
  return `${CACHE_KEY_PREFIX}${normalizedName}_${normalizedIngredients}`.replace(/[^a-z0-9_]/g, '_');
}

/**
 * Get cached analysis if available and not expired
 */
export function getCachedAnalysis(foodName: string, ingredientsText?: string): any | null {
  try {
    const cacheKey = getCacheKey(foodName, ingredientsText);
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) {
      return null;
    }
    
    const cachedData: CachedAnalysis = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is expired
    if (now - cachedData.timestamp > CACHE_EXPIRY_MS) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    console.log(`[foodAnalysisCache] Cache HIT for: ${foodName}`);
    
    // Reject previously-cached bad fallback results
    if (cachedData.analysis?.explanation?.includes('Please try again for detailed information')) {
      localStorage.removeItem(cacheKey);
      console.log(`[foodAnalysisCache] Evicted stale fallback for: ${foodName}`);
      return null;
    }
    
    return cachedData.analysis;
  } catch (error) {
    console.error('[foodAnalysisCache] Error reading cache:', error);
    return null;
  }
}

/**
 * Save analysis to cache
 */
export function saveCachedAnalysis(foodName: string, analysis: any, ingredientsText?: string): void {
  const cacheKey = getCacheKey(foodName, ingredientsText);
  const cachedData: CachedAnalysis = {
    foodName: foodName.trim().toLowerCase(),
    analysis,
    timestamp: Date.now(),
    ingredientsText: ingredientsText?.trim().toLowerCase(),
  };
  
  try {
    localStorage.setItem(cacheKey, JSON.stringify(cachedData));
    console.log(`[foodAnalysisCache] Cached analysis for: ${foodName}`);
    
    // Clean up old cache entries if we exceed max size
    cleanupCache();
  } catch (error) {
    console.error('[foodAnalysisCache] Error saving cache:', error);
    // If quota exceeded, clean up and try again
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      cleanupCache(true);
      try {
        localStorage.setItem(cacheKey, JSON.stringify(cachedData));
      } catch (retryError) {
        console.error('[foodAnalysisCache] Failed to save after cleanup:', retryError);
      }
    }
  }
}

/**
 * Clean up old cache entries
 */
function cleanupCache(aggressive: boolean = false): void {
  try {
    const cacheEntries: Array<{ key: string; timestamp: number }> = [];
    
    // Collect all cache entries
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const cachedData: CachedAnalysis = JSON.parse(cached);
            cacheEntries.push({ key, timestamp: cachedData.timestamp });
          }
        } catch (e) {
          // Invalid entry, remove it
          localStorage.removeItem(key!);
        }
      }
    }
    
    // Sort by timestamp (oldest first)
    cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
    
    // Remove oldest entries if we exceed max size
    if (cacheEntries.length > MAX_CACHE_SIZE || aggressive) {
      const toRemove = aggressive 
        ? Math.ceil(cacheEntries.length / 2) // Remove half if aggressive
        : cacheEntries.length - MAX_CACHE_SIZE;
        
      for (let i = 0; i < toRemove; i++) {
        localStorage.removeItem(cacheEntries[i].key);
      }
      
      console.log(`[foodAnalysisCache] Cleaned up ${toRemove} old cache entries`);
    }
  } catch (error) {
    console.error('[foodAnalysisCache] Error cleaning up cache:', error);
  }
}

/**
 * Clear all cached analyses
 */
export function clearCache(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`[foodAnalysisCache] Cleared ${keysToRemove.length} cache entries`);
  } catch (error) {
    console.error('[foodAnalysisCache] Error clearing cache:', error);
  }
}

